import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '@components/PrimaryButton';
import { useServiceItemsContext } from '@hooks';
import * as Sentry from '@sentry/react';
import OrderItemCard from '../OrderItemCard';
import LoadingSpinner from '../LoadingSpinner';
import ConfirmRefundModal from '../../../ConfirmRefundModal';
import {
  useStyles,
  useStylesSmallPrimaryButton,
  useStylesSmallPrimaryDisabledButton,
} from './styles';

const OrderItemsList = ({
  orderId,
  expanded,
  isOpenModal,
  orderItemData,
  isRunnerTypeUser,
  isLoadingOrderItemData,
  handleModalShow,
  handleModalClose,
  changeOrderGroupStatus,
}) => {
  const classes = useStyles();
  const classesSmallPrimaryButton = useStylesSmallPrimaryButton();
  const classesSmallPrimaryButtonDisabled = useStylesSmallPrimaryDisabledButton();
  const { t } = useTranslation('pointOfService');

  const [checkedOrderItemsRefund, setCheckedOrderItemsRefund] = useState([]);
  const [refundedAmount, setAmountRefunded] = useState(0);
  const [isTipChecked, setTipChecked] = useState(false);

  const { serviceItemsList } = useServiceItemsContext();

  const updateCheckedOrderItem = ({ id, productPrice, isTip }) => () => {
    if (isTip) {
      setTipChecked(!isTipChecked);
      return false;
    }
    const foundIdx = checkedOrderItemsRefund.indexOf(id);

    if (foundIdx === -1) {
      setAmountRefunded(prevState => {
        const sum = prevState + productPrice;
        return +sum.toFixed(2);
      });

      setCheckedOrderItemsRefund(prevState => [...prevState, id]);
      return;
    }

    setAmountRefunded(prevState => {
      const minuend = prevState - productPrice;
      return +minuend.toFixed(2);
    });

    setCheckedOrderItemsRefund(prevState =>
      prevState.filter(item => item !== id),
    );
  };

  if (expanded && isLoadingOrderItemData) return <LoadingSpinner />;

  if (expanded && !isLoadingOrderItemData && orderId !== orderItemData.id) {
    Sentry.captureException(new Error(t('serviceItemWithoutVisitId')), {
      tags: {
        orderId: orderId,
        'orderItemData.id': orderItemData.id,
      },
    });
    return (
      <div className={classes.messageAbsenceVisitId}>
        {t('serviceItemWithoutVisitId')}
      </div>
    );
  }

  return (
    <div className={classes.expandedOrderList}>
      <div>
        <div className={classes.refundHeader}>
          <p className={classes.refundTitle}>{t('description')}</p>
          {isRunnerTypeUser ? null : (
            <div className={classes.refundButtonWrapper}>
              <PrimaryButton
                text={t('buttons.refundButton')}
                classesButton={
                  checkedOrderItemsRefund.length === 0 && !isTipChecked
                    ? classesSmallPrimaryButtonDisabled
                    : classesSmallPrimaryButton
                }
                handleClick={handleModalShow(true)}
                isDisabledButton={
                  checkedOrderItemsRefund.length === 0 && !isTipChecked
                }
              />
            </div>
          )}
        </div>

        <div className={classes.orderItemsList}>
          {orderItemData.items.map(orderedProduct => {
            const orderItemData = {
              id: orderedProduct.id,
              productName: orderedProduct.menuItem.product.name,
              productPrice: orderedProduct.menuItem.priceIncludingTax,
              statusOrderItem: orderedProduct.status,
            };

            const foundServiceItem = serviceItemsList.find(
              item => item.id === orderedProduct.id,
            );

            return (
              <OrderItemCard
                orderItemData={orderItemData}
                status={t(`orderStatusList.${foundServiceItem.status}`)}
                isChecked={
                  checkedOrderItemsRefund.indexOf(orderedProduct.id) >= 0
                }
                isRunnerTypeUser={isRunnerTypeUser}
                key={orderedProduct.id}
                changeOrderGroupStatus={changeOrderGroupStatus}
                updateCheckedOrderItem={updateCheckedOrderItem}
              />
            );
          })}
          <OrderItemCard
            key={isTipChecked}
            orderItemData={orderItemData}
            isRunnerTypeUser={isRunnerTypeUser}
            isTipChecked={isTipChecked}
            isRefunded={orderItemData?.payment?.tipRefunded}
            changeOrderGroupStatus={changeOrderGroupStatus}
            updateCheckedOrderItem={updateCheckedOrderItem}
          />
        </div>
      </div>
      <ConfirmRefundModal
        open={isOpenModal}
        handleClose={handleModalShow}
        handleModalClose={handleModalClose}
        orderId={orderId}
        isTipChecked={isTipChecked}
        refundedAmount={refundedAmount}
        tipAmount={orderItemData?.payment?.tipAmount}
        refundItems={checkedOrderItemsRefund}
        itemsCount={checkedOrderItemsRefund.length}
      />
    </div>
  );
};

export default OrderItemsList;
